import React, { createContext, useContext, useMemo } from "react";

type TestIdScopeProps = {
  scope: string;
  children: React.ReactNode;
};

const TestIdContext = createContext("");

const joinTestId = (...parts: Array<string | false | null | undefined>) =>
  parts
    .filter(Boolean)
    .map((part) =>
      String(part)
        .trim()
        .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
        .replace(/[^a-zA-Z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase()
    )
    .filter(Boolean)
    .join("-");

export const TestIdScope = ({ scope, children }: TestIdScopeProps) => {
  const parentScope = useContext(TestIdContext);
  const value = useMemo(
    () => joinTestId(parentScope, scope),
    [parentScope, scope]
  );

  return (
    <TestIdContext.Provider value={value}>{children}</TestIdContext.Provider>
  );
};

export const useTid = () => {
  const scope = useContext(TestIdContext);
  return (id: string) => joinTestId(scope, id);
};

export const useTestAttrs = (id: string, ariaLabel?: string) => {
  const tid = useTid();
  return {
    "data-testid": tid(id),
    ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
  };
};

// Hook-rules-safe variant for components that build test attrs inside
// conditional JSX: call once at the top, then use the returned builder freely.
export const useTestAttrsFactory = () => {
  const tid = useTid();
  return (id: string, ariaLabel?: string) => ({
    "data-testid": tid(id),
    ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
  });
};
